import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import api from "../lib/api";
import { ArrowRight, Compass, Sailboat, GraduationCap, Anchor, Newspaper, Cloud } from "lucide-react";
import HomeEventPopup from "../components/HomeEventPopup";

export default function Home() {
  const { t, lang, img, visible, sectionOrder } = useLang();
  const ord = (id) => sectionOrder("home", id, ["quicklinks", "news", "pillars", "agenda", "weather"]);
  const [news, setNews] = useState([]);
  const [agenda, setAgenda] = useState([]);

  useEffect(() => {
    api.get("/news").then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
    api.get("/events").then((r) => setAgenda(r.data.slice(0, 4))).catch(() => {});
  }, []);

  const pillarIcons = [Compass, Sailboat, GraduationCap, Anchor];

  return (
    <div data-testid="home-page" className="flex flex-col">
      <HomeEventPopup />
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden">
        <img
          src={img("home", "hero", "https://images.unsplash.com/photo-1498623116890-37e912163d5d?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400")}
          alt="Sailing on the Meuse"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/70 to-navy/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pb-24 lg:pb-32 pt-32 text-white animate-fade-up w-full">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-club-yellow" />
            <div className="text-[11px] tracking-[0.3em] uppercase text-club-yellow font-medium">{t.home.eyebrow}</div>
          </div>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tight leading-[0.95] max-w-5xl whitespace-pre-line">
            {t.home.heroTitle}
          </h1>
          <p className="mt-8 text-base lg:text-lg text-slate-200 max-w-xl leading-relaxed">{t.home.heroSub}</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link to={t.home.ctaPrimaryLink || "/club#admission"} className="inline-flex items-center justify-center gap-2 bg-club-red text-white px-7 py-4 text-xs uppercase tracking-[0.25em] hover:bg-club-red-dark transition-colors" data-testid="hero-cta-primary">
              {t.home.ctaPrimary} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <Link to={t.home.ctaSecondaryLink || "/stages"} className="inline-flex items-center justify-center gap-2 bg-club-yellow text-navy px-7 py-4 text-xs uppercase tracking-[0.25em] hover:bg-club-yellow-dark transition-colors" data-testid="hero-cta-secondary">
              {t.home.ctaSecondary}
            </Link>
            <Link to={t.home.ctaTertiaryLink || "/agenda"} className="inline-flex items-center justify-center gap-2 border border-white/60 text-white px-7 py-4 text-xs uppercase tracking-[0.25em] hover:bg-white hover:text-navy transition-colors" data-testid="hero-cta-tertiary">
              {t.home.ctaTertiary}
            </Link>
          </div>
        </div>
      </section>

      {/* QUICK LINKS */}
      {visible("home", "quicklinks") && (
      <section style={{ order: ord("quicklinks") }} className="py-16 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {t.home.quickLinks.map((q, i) => (
              <Link
                key={q.path}
                to={q.path}
                className="group flex flex-col items-center justify-center gap-3 py-6 border border-slate-200 hover:border-club-red hover:bg-club-red/5 transition-colors"
                data-testid={`quicklink-${i}`}
              >
                <span className="text-xs uppercase tracking-[0.2em] text-navy group-hover:text-club-red transition-colors">{q.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* NEWS */}
      {visible("home", "news") && news.length > 0 && (
        <section style={{ order: ord("news") }} className="py-24 bg-sand">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
            <div className="flex items-end justify-between mb-12">
              <div>
                <div className="label-eyebrow text-club-red mb-4">{t.home.newsTitle}</div>
                <h2 className="font-serif text-4xl lg:text-5xl font-light text-navy">{t.home.newsSub}</h2>
              </div>
              <Newspaper className="w-8 h-8 text-club-yellow hidden md:block" strokeWidth={1.25} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {news.map((n, i) => (
                <article key={n.id} className="bg-white border border-slate-200 p-8 hover:border-club-red transition-colors" data-testid={`news-${i}`}>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-club-yellow-dark mb-3 font-semibold">
                    {n.category} · {new Date(n.created_at).toLocaleDateString(lang === "en" ? "en-GB" : lang)}
                  </div>
                  <h3 className="font-serif text-2xl text-navy mb-3">{n[`title_${lang}`]}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{n[`body_${lang}`]}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PILLARS */}
      {visible("home", "pillars") && (
      <section style={{ order: ord("pillars") }} className="py-24 bg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {t.home.pillars.map((p, i) => {
            const Icon = pillarIcons[i] || Compass;
            return (
              <div key={i} className="group" data-testid={`pillar-${i}`}>
                <Icon className="w-9 h-9 text-club-yellow mb-5" strokeWidth={1.25} />
                <h3 className="font-serif text-2xl mb-2">{p.title}</h3>
                <div className="w-10 h-px bg-club-red mb-3" />
                <p className="text-slate-300 text-sm leading-relaxed">{p.text}</p>
              </div>
            );
          })}
        </div>
      </section>
      )}

      {/* AGENDA */}
      {visible("home", "agenda") && agenda.length > 0 && (
        <section style={{ order: ord("agenda") }} className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
            <div className="flex items-end justify-between mb-12">
              <div>
                <div className="label-eyebrow text-club-red mb-4">{t.nav.agenda}</div>
                <h2 className="font-serif text-4xl lg:text-5xl font-light text-navy">{t.agenda.title}</h2>
              </div>
              <Link to="/agenda" className="text-xs uppercase tracking-[0.25em] text-navy border-b border-club-yellow pb-1 hover:text-club-red transition-colors" data-testid="home-see-agenda">
                {t.common.readMore} →
              </Link>
            </div>
            <div className="space-y-3">
              {agenda.map((e) => {
                const d = new Date(e.date);
                const dateStr = d.toLocaleDateString(lang === "en" ? "en-GB" : lang, { day: "2-digit", month: "long", year: "numeric" });
                const typeColor = e.type === "regatta" ? "bg-club-red" : e.type === "stage" ? "bg-club-yellow" : "bg-navy";
                return (
                  <div key={e.id} className="flex items-center gap-5 p-5 bg-sand border-l-4 border-club-yellow hover:bg-white hover:shadow-md transition-all" data-testid={`agenda-row-${e.id}`}>
                    <div className={`shrink-0 ${typeColor} text-white text-[10px] uppercase tracking-widest px-3 py-1.5 font-semibold ${e.type === "stage" ? "text-navy" : ""}`}>
                      {t.agenda.legend[e.type]}
                    </div>
                    <div className="flex-1">
                      <div className="font-serif text-xl text-navy">{e[`title_${lang}`]}</div>
                      <div className="text-xs text-slate-500 mt-1">{dateStr} · {e.location}</div>
                    </div>
                    {e.link && (
                      <Link to={e.link} className="text-xs uppercase tracking-widest text-club-red hover:text-club-red-dark">
                        →
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* WEATHER */}
      {visible("home", "weather") && (
      <section style={{ order: ord("weather") }} className="py-20 bg-sand">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <Cloud className="w-10 h-10 text-club-yellow mb-6" strokeWidth={1.25} />
            <h2 className="font-serif text-3xl lg:text-4xl font-light text-navy mb-4">{t.home.weatherTitle}</h2>
            <div className="divider-gold" />
            <p className="text-slate-600 mt-4 max-w-md">{t.home.weatherText}</p>
          </div>
          <div className="space-y-3">
            {t.home.weatherLinks.map((l) => (
              <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" className="block bg-white border border-slate-200 p-6 hover:border-club-red transition-colors group" data-testid={`weather-link-${l.label}`}>
                <div className="flex items-center justify-between">
                  <span className="font-serif text-xl text-navy group-hover:text-club-red transition-colors">{l.label}</span>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-club-red transition-colors" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
      )}
    </div>
  );
}
